import * as React from 'react';
import { Button, ColumnProps, Justify, Table } from '@rtkwlf/ui';
import { useDispatch, useSelector } from 'react-redux';
import { OBContact } from '../../../types';
import {
  getContactOption,
  getFullName,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { sessionSelector } from '../../../reducers/session';
import { apiRequest } from '../../../APIClient';
import Icon from '../../../widgets/Icons';
import { Tooltip } from '../../../widgets/Tooltip';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATH } from '../../../constants';
import { useSortTableName } from '../hooks/useSortTableName';
import { useEffect, useRef } from 'react';
import { ScrollSectionKeys } from '../site-config/OnboardingSiteConfig';

const getFullNameWithMSPSuffix = (contact: OBContact | null) => {
  if (contact) {
    const option = getContactOption('', contact);
    return (
      <>
        {option.label}
        {option.customElement}
      </>
    );
  }
  return null;
};

const getAccessControlDescription = (contactDetails: OBContact) => {
  const accessControls = [];
  if (contactDetails.isPortalUser) {
    accessControls.push('Portal');
  }
  if (contactDetails.isLogSearchUser) {
    accessControls.push('Data Explorer');
  }
  if (contactDetails.isRiskUser) {
    accessControls.push('Risk');
  }
  if (contactDetails.isManagedAwarenessUser) {
    accessControls.push('Managed Awareness');
  }
  if (contactDetails.isActivateVlcUser) {
    accessControls.push('Activate vLC');
  }
  return accessControls.join(', ');
};

const getPrimaryPhoneNumberString = (contact: OBContact): string => {
  if (contact.primaryNumber) {
    let result = contact.primaryNumber;
    if (contact.primaryExtensionNumber) {
      result += ` Ext. ${contact.primaryExtensionNumber}`;
    }
    return `${result} - ${contact.primaryNumberType}`;
  }
  return '';
};

export const ContactTypeInfo: React.FunctionComponent<{
  contactDetails: OBContact;
}> = ({ contactDetails }) => {
  return (
    <div data-cy="contactIcon">
      {contactDetails.isDL ? (
        <Icon.Users className="mr-1 mb-1" />
      ) : contactDetails.isShippingContact ? (
        <Icon.UserTag className="mr-1 mb-1" />
      ) : (
        <Icon.User className="mr-1 mb-1" />
      )}
      {contactDetails.isDL
        ? 'List'
        : contactDetails.isShippingContact
        ? 'Shipping'
        : 'User'}
    </div>
  );
};

export type TableData = {
  key: string;
  type: OBContact;
  displayName: OBContact;
  name: string;
  email: string;
  phoneNumber: string;
  accessControls: string;
  isMSP: boolean;
};

export const OnboardingContactList = () => {
  const contactsSectionRef = useRef<null | HTMLDivElement>(null);
  const contacts = useSelector(onboardingSelector.getContactsWithoutMSP);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);

  const history = useHistory();

  useEffect(() => {
    const { location } = history;
    if (location.search === `view=${ScrollSectionKeys.CONTACTS}`) {
      setTimeout(() => {
        contactsSectionRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      }, 100);
    }
  }, [contactsSectionRef, history]);

  const onAdd = React.useCallback(
    () => history.push(ROUTE_PATH.ONBOARDING.CONTACTS.NEW),
    [history],
  );

  const onEdit = React.useCallback(
    (uuid) => history.push(`${ROUTE_PATH.ONBOARDING.CONTACTS.EDIT}/${uuid}`),
    [history],
  );

  const editButtonOnClick = React.useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const id = e.currentTarget.dataset.id;
      if (id) {
        onEdit(id);
      }
    },
    [onEdit],
  );
  const { sort, handleSort } = useSortTableName<TableData>();
  const formattedContacts = React.useMemo(() => {
    return sort(
      Object.keys(contacts).map((uuid: string) => {
        const contact = contacts[uuid];
        return {
          key: uuid,
          type: contact,
          displayName: contact,
          name: getFullName(contact),
          email: contact.email,
          phoneNumber: getPrimaryPhoneNumberString(contact),
          accessControls: getAccessControlDescription(contact),
          isMSP: contact.isMSP,
        };
      }),
    );
  }, [contacts, sort]);

  const columns: ColumnProps<TableData>[] = React.useMemo(() => {
    const result: ColumnProps<TableData>[] = [
      {
        key: 'type',
        title: 'Type',
        render: ({ record: { type } }) => (
          <ContactTypeInfo contactDetails={type} />
        ),
      },
      {
        key: 'displayName',
        render: ({ record: { displayName } }) =>
          getFullNameWithMSPSuffix(displayName),
        title: 'Display Name',
        sortable: true,
      },
      {
        key: 'email',
        dataIndex: 'email',
        title: 'Email',
      },
      {
        key: 'phoneNumber',
        dataIndex: 'phoneNumber',
        title: 'Primary Phone Number',
      },
      {
        key: 'accessControls',
        dataIndex: 'accessControls',
        title: 'Access Controls',
      },
    ];
    if (canEdit) {
      result.push({
        key: 'actions',
        title: 'Actions',
        width: 5,
        render: ({ record: { key, isMSP } }) => {
          return (
            <div className="d-flex mr-2">
              {!isMSP && (
                <Icon.Pencil
                  data-cy="editContact"
                  style={{ cursor: 'pointer' }}
                  data-id={key}
                  onClick={editButtonOnClick}
                />
              )}
              {isMSP && (
                <Tooltip
                  text="Please contact your CST to make changes."
                  placement="left"
                  icon={<Icon.Pencil />}
                />
              )}
            </div>
          );
        },
        justify: 'flex-end' as Justify,
      });
    }
    return result;
  }, [canEdit, editButtonOnClick]);

  return (
    <>
      {canEdit && (
        <div className="pb-2 text-right" ref={contactsSectionRef}>
          <p className="ob-section-description float-left">
            Add contacts such as users and distribution lists for
            administration, escalation, reporting, and shipping.
          </p>
          <Button
            type="primary"
            ghost
            onClick={onAdd}
            className="float-right mb-2"
            data-cy="addContact"
          >
            <Icon.Plus className="mr-2" />
            Add Contact
          </Button>
        </div>
      )}
      <Table
        data={formattedContacts}
        dataUniqueKey="value"
        columns={columns}
        onSort={handleSort}
        emptyComponent={
          canEdit && (
            <div
              className="my-5 text-center"
              data-cy="add-contact-table-placeholder"
            >
              <span>
                Select
                <Button
                  type="link"
                  className="d-inline px-1 font-normal"
                  onClick={onAdd}
                >
                  Add Contact
                </Button>
                to add a user or distribution list
              </span>
            </div>
          )
        }
      />
    </>
  );
};

export const ContactListFooter: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const completed = useSelector(onboardingSelector.getContactsCompleted);

  const onMarkAsComplete = React.useCallback(() => {
    dispatch(apiRequest.toggleOBContactsComplete());
  }, [dispatch]);

  const contacts = useSelector(onboardingSelector.getContactsWithoutMSP);
  const canBeMarkedAsComplete = Object.values(contacts).length > 0;
  const disabled = !canEdit || (!canBeMarkedAsComplete && !completed);
  const buttonText = completed ? 'Mark Incomplete' : 'Mark Complete';

  return (
    <div className="ob-mark-complete-button">
      <Button
        type="primary"
        ghost={completed}
        disabled={disabled}
        onClick={onMarkAsComplete}
        data-cy="contactsListCompleteButton"
      >
        {buttonText}
      </Button>
    </div>
  );
};

export default OnboardingContactList;
